import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ArrowLeft, KeyRound, Eye, EyeOff } from "lucide-react";
import { ToThaiLogo } from "@/components/ToThaiLogo";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (next.length < 10) { setError("Hasło musi mieć minimum 10 znaków."); return; }
    if (next !== confirm) { setError("Hasła nie pasują."); return; }
    if (!token) { setError("Brak tokenu w linku."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: next });
      toast.success("Hasło zmienione. Zaloguj się.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || "Link nieważny lub wygasł.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--tt-bg)] p-6">
      <Card className="tt-card w-full max-w-md" data-testid="reset-password-page">
        <CardHeader>
          <div className="flex items-center justify-center mb-3">
            <ToThaiLogo layout="inline" size="sm" />
          </div>
          <CardTitle className="tt-display text-center">Nowe hasło</CardTitle>
          <CardDescription className="text-center">
            Ustaw nowe hasło dla swojego konta. Wszystkie sesje zostaną wylogowane.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label>Nowe hasło (min. 10 znaków)</Label>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  required minLength={10}
                  autoComplete="new-password"
                  className="pr-10"
                  data-testid="reset-new-password-input"
                />
                <button type="button" onClick={() => setShowPw((s) => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--tt-muted)] hover:text-[var(--tt-text)]"
                        aria-label="Pokaż hasło">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Potwierdź nowe hasło</Label>
              <Input
                type={showPw ? "text" : "password"}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required minLength={10}
                autoComplete="new-password"
                data-testid="reset-confirm-password-input"
              />
            </div>
            {error && (<Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>)}
            <Button type="submit" className="w-full" disabled={loading} data-testid="reset-password-submit-button">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Ustaw nowe hasło
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-[var(--tt-muted)] hover:text-[var(--tt-text)] inline-flex items-center gap-1">
              <ArrowLeft className="h-3.5 w-3.5" /> Wróć do logowania
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
